import 'twin.macro'

import LoanPaymentDocKK from 'assets/files/loan_payment_doc_kk.pdf'
import LoanPaymentDocRU from 'assets/files/loan_payment_doc_ru.pdf'
import PdfIcon from 'assets/images/landing/pdf.svg?component'
import OutlinedButton from 'components/UI/Landing/button-outlined'
import PrimaryButton from 'components/UI/Landing/button-primary'
import Container from 'components/UI/Landing/container'
import SectionTitle from 'components/UI/Landing/section-title'
import Link from 'components/UI/Link'
import FaqItem from 'components/views/Landing/faq-item.view'
import { ReactNode, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { randomId } from 'utils'

export type Qna = {
  id: string
  question: string
  answer: string | ReactNode | null
}

const getFileLink = (language: string) => {
  if (language === 'kk') return LoanPaymentDocKK
  else if (language === 'ru') return LoanPaymentDocRU
  return ''
}

const getExternalLoanLink = (language: string) => {
  if (language === 'kk') return 'https://bankffin.kz/kk/loans'
  else if (language === 'ru') return 'https://bankffin.kz/ru/loans'
  return ''
}

const getStatgovLink = (language: string) => {
  if (language === 'kk') return 'https://stat.gov.kz/juridical/by/bin/'
  else if (language === 'ru') return 'https://stat.gov.kz/ru/juridical/by/bin/'
  return ''
}

export default function FaqSection(): JSX.Element {
  const {
    t,
    i18n: { language }
  } = useTranslation('landing')

  const qnas = useMemo(
    (): Qna[] => [
      {
        id: randomId(),
        question: t('faq-section.first-q'),
        answer: (
          <div>
            <ul tw='ml-4 list-disc'>
              <li>{t('faq-section.first-q-a')}</li>
              <li>{t('faq-section.first-q-a-1')}</li>
              <li>{t('faq-section.first-q-a-2')}</li>
              <ul>
                <li>{t('faq-section.first-q-a-2_1')}</li>
                <li>{t('faq-section.first-q-a-2_2')}</li>
                <li>{t('faq-section.first-q-a-2_3')}</li>
                <li>{t('faq-section.first-q-a-2_4')}</li>
                <li>{t('faq-section.first-q-a-2_5')}</li>
              </ul>

              <li>{t('faq-section.first-q-a-3')}</li>
              <ul>
                <li>{t('faq-section.first-q-a-3_1')}</li>
                <li>{t('faq-section.first-q-a-3_2')}</li>
              </ul>
            </ul>
            {t('faq-section.first-q-a-4')}.{' '}
            <Trans
              components={{
                a: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    href={
                      language === 'ru'
                        ? `https://damu.kz/ru/programmi/subsidy/enterprise_development/micro_subsidy`
                        : 'https://damu.kz/programmi/subsidy/enterprise_development/micro_subsidy'
                    }
                    target='_blank'
                    rel='noreferrer'
                    tw='underline'
                  />
                )
              }}
            >
              {t('faq-section.first-q-a-5')}
            </Trans>
          </div>
        )
      },
      {
        id: randomId(),
        question: t('faq-section.second-q'),
        answer: (
          <ul tw='ml-4 list-disc'>
            <li>{t('faq-section.first-q-a')}</li>
            <li>{t('faq-section.first-q-a-1')}</li>
            <li>{t('faq-section.first-q-a-2')}</li>
            <ul>
              <li>{t('faq-section.second-q-a-2')}</li>
              <li>{t('faq-section.second-q-a-3')}</li>
            </ul>
            <li>{t('faq-section.first-q-a-2_3')}</li>
            <li>{t('faq-section.second-q-a.1')}</li>
            <li>{t('faq-section.second-q-a.2')}</li>
            <li>{t('faq-section.second-q-a-1')}</li>
          </ul>
        )
      },
      {
        id: randomId(),
        question: t('faq-section.tenth-q'),
        answer: (
          <div>
            <p>{t('faq-section.tenth-q-a')}</p>
            <ul tw='ml-4 mt-2 list-disc space-y-1'>
              <li>
                <p tw='font-semibold'>{t('faq-section.tenth-q-a-1')}</p>
                <span>{t('faq-section.tenth-q-a-1-text')}</span>
              </li>
              <li>
                <p tw='font-semibold'>{t('faq-section.tenth-q-a-2')}</p>
                <span>
                  <Trans
                    components={{
                      a: (
                        <Link
                          href={getExternalLoanLink(language)}
                          target='_blank'
                          rel='noreferrer'
                          onClick={e => e.stopPropagation()}
                        >
                          {t('flow-agreement-personal-data2')}{' '}
                        </Link>
                      )
                    }}
                  >
                    {t('faq-section.tenth-q-a-2-text')}
                  </Trans>
                </span>
              </li>
              <li>
                <p tw='font-semibold'>{t('faq-section.tenth-q-a-3')}</p>
                <span>{t('faq-section.tenth-q-a-3-text')}</span>
              </li>
              <li>
                <p tw='font-semibold'>{t('faq-section.tenth-q-a-4')}</p>
                <span>{t('faq-section.tenth-q-a-4-text')}</span>
              </li>
            </ul>
            <OutlinedButton
              className='mt-6 flex items-center justify-center font-semibold  gap-2 border-[var(--text-landing-green)] text-[var(--text-landing-green)] hover:bg-gradient-to-r hover:text-[white] transition-all duration-300 from-green-dark-landing to-green-light-landing'
              onClick={e => {
                e.stopPropagation()
                window.open(getFileLink(language))
              }}
            >
              {t('faq-section.tenth-q-btn-text')}
              <PdfIcon />
            </OutlinedButton>
          </div>
        )
      },
      {
        id: randomId(),
        question: t('faq-section.third-q'),
        answer: `${t('faq-section.third-q-a')} ${t('faq-section.third-q-a-2')}`
      },
      {
        id: randomId(),
        question: t('faq-section.fourth-q'),
        answer: `${t('faq-section.fourth-q-a')}`
      },
      {
        id: randomId(),
        question: t('faq-section.fifth-q'),
        answer: (
          <div>
            {t('faq-section.fifth-q-a')} <br />
            {t('faq-section.fifth-q-a-2')}
          </div>
        )
      },
      {
        id: randomId(),
        question: t('faq-section.sixth-q'),
        answer: t('faq-section.sixth-q-a')
      },
      {
        id: randomId(),
        question: t('faq-section.seventh-q'),
        answer: t('faq-section.seventh-q-a')
      },
      {
        id: randomId(),
        question: t('faq-section.eighth-q'),
        answer: t('faq-section.eighth-q-a')
      },
      {
        id: randomId(),
        question: t('faq-section.ninth-q'),
        answer: (
          <div>
            {t('faq-section.ninth-q-a')}{' '}
            <a
              href='https://online.bankffin.kz/login?continue=%2Faccount%2Flist'
              target='_blank'
              rel='noreferrer'
              tw='underline'
            >
              {t('faq-section.ninth-q-a-1')}
            </a>{' '}
            {t('faq-section.ninth-q-a-2')}
          </div>
        )
      },
      {
        id: randomId(),
        question: t('faq-section.eleventh-q'),
        answer: (
          <div>
            {t('faq-section.eleventh-q-a-1')}{' '}
            <a href='https://bankffin.kz/' target='_blank' rel='noreferrer' tw='underline'>
              {t('faq-section.eleventh-q-a-2')}
            </a>{' '}
            {t('faq-section.eleventh-q-a-3')}
            <ol tw='list-decimal ml-5'>
              <li>{t('faq-section.eleventh-q-a-4.1')}</li>
              <li>{t('faq-section.eleventh-q-a-4.2')}</li>
              <li>{t('faq-section.eleventh-q-a-4.3')}</li>
            </ol>
            {t('faq-section.eleventh-q-a-4.4')}
          </div>
        )
      },
      {
        id: randomId(),
        question: t('faq-section.twelfth-q'),
        answer: t('faq-section.twelfth-q-a')
      },
      {
        id: randomId(),
        question: t('faq-section.thirteenth-q'),
        answer: t('faq-section.thirteenth-q-a')
      },
      {
        id: randomId(),
        question: t('faq-section.fourteenth-q'),
        answer: (
          <div>
            {t('faq-section.fourteenth-q-a')}{' '}
            <a href={getStatgovLink(language)} target='_blank' rel='noreferrer' tw='underline'>
              {t('faq-section.fourteenth-q-a-1')}
            </a>
          </div>
        )
      },
      {
        id: randomId(),
        question: t('faq-section.fifteenth-q'),
        answer: t('faq-section.fifteenth-q-a-1')
      }
    ],
    [language, t]
  )
  return (
    <section className='py-16'>
      <Container>
        <SectionTitle title={t('faq-section.title')} />
        <div className='mt-10'>
          {qnas.map(item => (
            <FaqItem key={item.id} {...item} />
          ))}
        </div>
        <PrimaryButton
          type='button'
          className='w-full sm:w-auto mt-5'
          onClick={() => {
            window.scrollTo({
              behavior: 'smooth',
              top: 800
            })
          }}
        >
          {t('faq-section.btn-text')}
        </PrimaryButton>
      </Container>
    </section>
  )
}
